import * as THREE from 'three';
import { darkenColor as darkenColorInt, lightenColor, DEFAULT_BUILDING_WIDTH } from './buildingGeometry';
import { darkenHexColor, lightenHexColor } from '../common/colorUtils';

// Create shared geometries to reuse
const cubeSize = 1;
const sharedCubeGeometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
const sharedEdgesGeometry = new THREE.EdgesGeometry(sharedCubeGeometry);

/**
 * facadeGeometry.js
 * 
 * This module contains utility functions for generating the 3D geometry of building facades
 * using Three.js. It provides helper functions to create various facade elements like:
 * 
 * - Windows and window frames
 * - Doors and entryways 
 * - Balconies and decorative elements
 * - Facade materials and textures
 * 
 * The module uses shared geometries (like cubes) that are reused across different
 * facade elements to improve performance. It works with the BuildingContext to get
 * styling parameters and integrates with the Three.js rendering system.
 * 
 * Key features:
 * - Efficient geometry reuse through shared geometries
 * - Modular functions for different facade components
 * - Integration with building styling system
 * - Support for dynamic updates and modifications
 */

/**
 * Creates a window cube
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} z - Z position
 * @param {THREE.Material} material - Material for the window
 * @param {boolean} isFrame - Whether this is a window frame
 * @returns {THREE.Mesh} Window mesh
 */
const createWindow = (x, y, z, material, isFrame = false) => {
  const windowCube = new THREE.Mesh(sharedCubeGeometry, material);
  windowCube.position.set(x, y, z);
  windowCube.name = 'dynamic';
  return windowCube;
};

/**
 * Creates a door mesh
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} z - Z position
 * @param {number} widthDoor - Width of the door
 * @param {number} heightDoor - Height of the door
 * @param {THREE.Material} material - Material for the door
 * @returns {THREE.Mesh} Door mesh
 */
const createDoor = (x, y, z, widthDoor, heightDoor, material) => {
  // Door needs a custom geometry based on its size
  const doorGeometry = new THREE.BoxGeometry(widthDoor * cubeSize, heightDoor * cubeSize, cubeSize);
  const door = new THREE.Mesh(doorGeometry, material);
  door.position.set(
    x - 0.5 + (widthDoor * cubeSize) / 2,
    y - 0.5 + (heightDoor * cubeSize) / 2,
    z
  );
  door.name = 'dynamic';
  return door;
};

/**
 * Creates a balcony cube
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} z - Z position
 * @param {THREE.Material} material - Material for the balcony
 * @returns {THREE.Mesh} Balcony mesh
 */
const createBalcony = (x, y, z, material) => {
  const balconyCube = new THREE.Mesh(sharedCubeGeometry, material);
  balconyCube.position.set(x, y, z);
  balconyCube.name = 'dynamic';
  return balconyCube;
};

/**
 * Creates an awning cube
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} z - Z position
 * @param {THREE.Material} material - Material for the awning
 * @returns {THREE.Mesh} Awning mesh
 */
const createAwning = (x, y, z, material) => {
  const awningCube = new THREE.Mesh(sharedCubeGeometry, material);
  awningCube.position.set(x, y, z);
  awningCube.name = 'dynamic';
  return awningCube;
};

/**
 * Creates a vitrine cube
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} z - Z position
 * @param {THREE.Material} material - Material for the vitrine
 * @returns {THREE.Mesh} Vitrine mesh
 */
const createVitrine = (x, y, z, material) => {
  const vitrineCube = new THREE.Mesh(sharedCubeGeometry, material);
  vitrineCube.position.set(x, y, z);
  vitrineCube.name = 'dynamic';
  return vitrineCube;
};

/**
 * Creates a group containing all the building walls, windows, doors, and other features
 * @param {Object} params - Building parameters
 * @param {number} [params.width=DEFAULT_BUILDING_WIDTH] - The width of the building (default: 42)
 * @returns {THREE.Group} A group containing all building meshes
 */
export const createWallsGroup = (params) => {
  const {
    levelHeight,
    groundFloorHeight,
    depth,
    levelNum,
    windowConfig,
    showLevelLines,
    facadeColor,
    windowTop,
    windowBottom,
    groundFloorColor,
    doorHeight,
    doorWidth,
    WindowColorA,
    WindowColorB,
    doorPosition,
    sideWallColor,
    showEdges,
    showBalconies,
    balconyColor,
    balconyDepth,
    balconyHeight,
    balconySideOffset,
    showAwnings,
    awningColor,
    awningDepth,
    awningHeight,
    awningSideOffset,
    roofColor,
    guardrailColor,
    showRoof,
    showWindowFrames,
    windowFrameColor,
    levelLinesColor = '#333333', // Default to dark gray if not provided
    roofAwning,
    roofThickness,
    roofAccessColor,
    showRoofAccess = true, // Default to true for backward compatibility
    roofAccessWidth,
    roofAccessHeight,
    roofAccessDepth,
    roofAccessOffset,
    roofAccessDoorWidth = 3, // Default door width for roof access
    roofAccessDoorHeight = 5, // Default door height for roof access
    showVitrine,
    vitrineColor,
    vitrineOffset = 0,
    showVitrineFrames,
    vitrineFrameDivision,
    showVitrineAwning,
    vitrineAwningDepth,
    vitrineAwningHeight,
    vitrineAwningColor,
    vitrineFrameColor,
    windowInset = 0,
    showLeftSideWindows,
    showRightSideWindows,
    showBrickPattern = false, // Default to false for backward compatibility
    brickContrast = 0.1, // Default contrast level
    randomEraseBricks = 0, // Random brick erasure level (0-1), default to 0
    randomEraseFine = 0, // Fine-grained individual brick erasure (0-1), default to 0
    brickOnSideWalls = true, // Apply pattern to side walls, default true for backward compatibility
    brickOnFacade = false, // Apply pattern to facade, default false
    brickOnGroundFloor = false, // Apply pattern to ground floor, default false
    width = DEFAULT_BUILDING_WIDTH
  } = params;
  
  const group = new THREE.Group();

  const facadeColorInt = parseInt(facadeColor.replace('#', ''), 16);
  const groundFloorColorInt = parseInt(groundFloorColor.replace('#', ''), 16);
  const doorColorInt = darkenColorInt(groundFloorColorInt, 0.5);
  const WindowColorAInt = parseInt(WindowColorA.replace('#', ''), 16);
  const WindowColorBInt = parseInt(WindowColorB.replace('#', ''), 16);
  const sideWallColorInt = parseInt(sideWallColor.replace('#', ''), 16);
  
  // Create brick color based on contrast level
  let brickColorInt;
  if (brickContrast > 0) {
    // Positive contrast - lighten the color
    brickColorInt = parseInt(lightenHexColor(sideWallColor, Math.abs(brickContrast)).replace('#', ''), 16);
  } else if (brickContrast < 0) {
    // Negative contrast - darken the color
    brickColorInt = parseInt(darkenHexColor(sideWallColor, Math.abs(brickContrast)).replace('#', ''), 16);
  } else {
    // Zero contrast - use the same color
    brickColorInt = sideWallColorInt;
  }
  
  const balconyColorInt = parseInt(balconyColor.replace('#', ''), 16);
  const awningColorInt = parseInt(awningColor.replace('#', ''), 16);
  const vitrineColorInt = parseInt(vitrineColor.replace('#', ''), 16);
  const vitrineAwningColorInt = parseInt(vitrineAwningColor.replace('#', ''), 16);
  const levelLinesColorInt = parseInt(levelLinesColor.replace('#', ''), 16);
  const guardrailColorInt = guardrailColor ? parseInt(guardrailColor.replace('#', ''), 16) : 
                           parseInt(lightenColor(roofColor, 0.2).replace('#', ''), 16);

  const materials = {
    facade: new THREE.MeshStandardMaterial({ color: facadeColorInt }),
    courtyard: new THREE.MeshStandardMaterial({ color: facadeColorInt }),
    sideWall: new THREE.MeshStandardMaterial({ color: sideWallColorInt }),
    brick: new THREE.MeshStandardMaterial({ color: brickColorInt }),
    windowColorA: new THREE.MeshStandardMaterial({ color: WindowColorAInt }),
    windowColorB: new THREE.MeshStandardMaterial({ color: WindowColorBInt }),
    brown: new THREE.MeshStandardMaterial({ color: groundFloorColorInt }),
    levelLineFrontBack: new THREE.MeshStandardMaterial({ color: levelLinesColorInt }),
    levelLineSide: new THREE.MeshStandardMaterial({ color: levelLinesColorInt }),
    door: new THREE.MeshStandardMaterial({ color: doorColorInt }),
    balcony: new THREE.MeshStandardMaterial({ color: balconyColorInt }),
    awning: new THREE.MeshStandardMaterial({ color: awningColorInt }),
    roof: new THREE.MeshStandardMaterial({ color: parseInt(roofColor.replace('#', ''), 16) }),
    guardrail: new THREE.MeshStandardMaterial({ color: guardrailColorInt }),
    windowFrame: new THREE.MeshStandardMaterial({ 
      color: parseInt(windowFrameColor.replace('#', ''), 16) 
    }),
    roofAccess: new THREE.MeshStandardMaterial({
      color: parseInt(roofAccessColor.replace('#', ''), 16)
    }),
    roofAccessDoor: new THREE.MeshStandardMaterial({
      color: guardrailColorInt
    }),
    vitrine: new THREE.MeshStandardMaterial({ color: vitrineColorInt }),
    vitrineAwning: new THREE.MeshStandardMaterial({ color: vitrineAwningColorInt }),
    vitrineFrame: new THREE.MeshStandardMaterial({ color: vitrineFrameColor }),
  };

  // Helper to add windows (and balconies/awnings) for a given level
  const addWindows = (group, yOffset, windowColorB) => {
    const windowYOffset = 5;
    let windows = [];
    
    // Track window positions to avoid placing facade voxels there
    const windowPositions = new Set();

    const createWindowConfig = (windowWidth, numWindows, spacing) => {
      // For config6, shift the whole row by 1 unit
      const shift = windowConfig === 'config6' ? 1 : 0;
      const startX = Math.floor((width - (numWindows * windowWidth + (numWindows - 1) * spacing)) / 2) + shift;
      const windowsArr = [];
      
      // Create window groups for each material to batch windows
      const windowAGroup = new THREE.Group();
      const windowBGroup = new THREE.Group();
      const frameGroup = new THREE.Group();
      
      // Create window voxels on both front and back facades
      for (let i = 0; i < numWindows; i++) {
        for (let x = 0; x < windowWidth; x++) {
          for (let y = windowBottom; y < windowTop; y++) {
            const isFrame = showWindowFrames && (
              x === 0 || // left edge
              x === windowWidth - 1 || // right edge
              y === windowBottom || // bottom edge
              y === windowTop - 1 // top edge
            );
            
            const mat = isFrame ? materials.windowFrame : 
                      (x < windowWidth / 2 ? materials.windowColorA : windowColorB);
            
            const currentX = startX + i * (windowWidth + spacing) + x;
            const currentY = yOffset + windowYOffset + y;
            
            // For front facade
            if (isFrame) {
              // Window frames always stay on the facade
              const frameCube = createWindow(
                currentX,
                currentY,
                0,
                mat,
                isFrame
              );
              frameGroup.add(frameCube);
            } else {
              // Regular window (inset)
              const windowCube = createWindow(
                currentX,
                currentY,
                0 + windowInset,
                mat,
                isFrame
              );
              
              if (x < windowWidth / 2) {
                windowAGroup.add(windowCube);
              } else {
                windowBGroup.add(windowCube);
              }
              
              // Track window position to avoid placing facade voxels here
              if (windowInset > 0) {
                for (let insetZ = 0; insetZ < windowInset; insetZ++) {
                  const posKey = `${currentX}_${currentY}_${insetZ}`;
                  windowPositions.add(posKey);
                }
              }
            }
            
            // For back facade - same batching approach
            if (isFrame) {
              // Window frames always stay on the facade
              const frameCube = createWindow(
                currentX,
                currentY,
                (depth - 1) * cubeSize,
                mat,
                isFrame
              );
              frameGroup.add(frameCube);
            } else {
              // Regular window (inset)
              const windowCube = createWindow(
                currentX,
                currentY,
                (depth - 1) * cubeSize - windowInset,
                mat,
                isFrame
              );
              
              if (x < windowWidth / 2) {
                windowAGroup.add(windowCube);
              } else {
                windowBGroup.add(windowCube);
              }
              
              // Track window position to avoid placing facade voxels here
              if (windowInset > 0) {
                for (let insetZ = 0; insetZ < windowInset; insetZ++) {
                  const posKey = `${currentX}_${currentY}_${(depth - 1) - insetZ}`;
                  windowPositions.add(posKey);
                }
              }
            }
          }
        }
      }
      
      // Add all groups to the main group
      windowsArr.push(windowAGroup);
      windowsArr.push(windowBGroup);
      windowsArr.push(frameGroup);
      
      // Add balcony voxels if enabled
      if (showBalconies) {
        for (let h = 0; h < balconyHeight; h++) {
          const currentBalconyY = yOffset + windowYOffset + windowBottom - 1 - h;
          for (let i = 0; i < numWindows; i++) {
            const balconyStartX = startX + i * (windowWidth + spacing) - balconySideOffset;
            const balconyWindowWidth = windowWidth + (balconySideOffset * 2);
            for (let x = 0; x < balconyWindowWidth; x++) {
              for (let d = 0; d < balconyDepth; d++) {
                // On the facade, shift into negative Z; on the courtyard, shift positive
                windowsArr.push(createBalcony(balconyStartX + x, currentBalconyY, -1 - d, materials.balcony));
                windowsArr.push(createBalcony(balconyStartX + x, currentBalconyY, (depth - 1) * cubeSize + 1 + d, materials.balcony));
              }
            }
          }
        }
      }
      
      // Add awning voxels if enabled (mirrored vertically above the window)
      if (showAwnings) {
        for (let h = 0; h < awningHeight; h++) {
          const currentAwningY = yOffset + windowYOffset + windowTop + h;
          for (let i = 0; i < numWindows; i++) {
            const awningStartX = startX + i * (windowWidth + spacing) - awningSideOffset;
            const awningWindowWidth = windowWidth + (awningSideOffset * 2);
            for (let x = 0; x < awningWindowWidth; x++) {
              for (let d = 0; d < awningDepth; d++) {
                windowsArr.push(createAwning(awningStartX + x, currentAwningY, -1 - d, materials.awning));
                windowsArr.push(createAwning(awningStartX + x, currentAwningY, (depth - 1) * cubeSize + 1 + d, materials.awning));
              }
            }
          }
        }
      }
      
      return { windowsArr, windowPositions };
    };

    // Create window configuration based on the specified config
    let windowPositionsSet = new Set();
    
    if (windowConfig === 'config4') {
      const result = createWindowConfig(8, 4, 2);
      windows = result.windowsArr;
      windowPositionsSet = result.windowPositions;
    } else if (windowConfig === 'config5') {
      const result = createWindowConfig(6, 5, 2);
      windows = result.windowsArr;
      windowPositionsSet = result.windowPositions;
    } else if (windowConfig === 'config2Large') {
      const margin = 4;
      const availableWidth = width - margin * 3; 
      const windowWidth = Math.floor(availableWidth / 2);
      const result = createWindowConfig(windowWidth, 2, margin);
      windows = result.windowsArr;
      windowPositionsSet = result.windowPositions;
    } else if (windowConfig === 'config6') {
      const windowWidth = 8;
      const numWindows = 5;
      const shift = 0;
      const startX = Math.floor((width - (numWindows * windowWidth)) / 2) + shift;
      const windowsArr = [];
      
      for (let i = 0; i < numWindows; i++) {
        for (let x = 0; x < windowWidth; x++) {
          for (let y = windowBottom; y < windowTop; y++) {
            const mat = x < windowWidth / 2 ? materials.windowColorA : windowColorB;
            windowsArr.push(createWindow(startX + i * windowWidth + x, yOffset + windowYOffset + y, 0, mat));
            windowsArr.push(createWindow(startX + i * windowWidth + x, yOffset + windowYOffset + y, (depth - 1) * cubeSize, mat));
          }
        }
      }
      
      if (showBalconies) {
        for (let h = 0; h < balconyHeight; h++) {
          const currentBalconyY = yOffset + windowYOffset + windowBottom - 1 - h;
          for (let i = 0; i < numWindows; i++) {
            const balconyStartX = startX + i * windowWidth - balconySideOffset;
            const balconyWindowWidth = windowWidth + (balconySideOffset * 2);
            for (let x = 0; x < balconyWindowWidth; x++) {
              for (let d = 0; d < balconyDepth; d++) {
                windowsArr.push(createBalcony(balconyStartX + x, currentBalconyY, -1 - d, materials.balcony));
                windowsArr.push(createBalcony(balconyStartX + x, currentBalconyY, (depth - 1) * cubeSize + 1 + d, materials.balcony));
              }
            }
          }
        }
      }
      
      if (showAwnings) {
        for (let h = 0; h < awningHeight; h++) {
          const currentAwningY = yOffset + windowYOffset + windowTop + h;
          for (let i = 0; i < numWindows; i++) {
            const awningStartX = startX + i * windowWidth - awningSideOffset;
            const awningWindowWidth = windowWidth + (awningSideOffset * 2);
            for (let x = 0; x < awningWindowWidth; x++) {
              for (let d = 0; d < awningDepth; d++) {
                windowsArr.push(createAwning(awningStartX + x, currentAwningY, -1 - d, materials.awning));
                windowsArr.push(createAwning(awningStartX + x, currentAwningY, (depth - 1) * cubeSize + 1 + d, materials.awning));
              }
            }
          }
        }
      }
      
      windows = windowsArr;
    }
    
    windows.forEach(windowObj => group.add(windowObj));
    
    return windowPositionsSet;
  };

  // Function to create walls - modified to batch wall creation
  const createWall = (material, zPos, isFrontBack, yOffset, currentLevelHeight, doorX, isDoorLevel, windowPositions) => {
    // Create a group for the wall voxels
    const wallGroup = new THREE.Group();
    
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < currentLevelHeight; y++) {
        // Skip door area
        if (isDoorLevel && y < doorHeight && x >= doorX && x < doorX + doorWidth) {
          continue;
        }
        
        // Skip window inset areas
        const currentZ = zPos / cubeSize;
        const posKey = `${x}_${y + yOffset / cubeSize}_${currentZ}`;
        if (windowPositions.has(posKey)) {
          continue;
        }
        
        // Determine which material to use
        let wallMaterial = material;
        
        // Apply level lines if enabled
        if (showLevelLines && y === currentLevelHeight - 1) {
          wallMaterial = isFrontBack ? materials.levelLineFrontBack : materials.levelLineSide;
        }
        // Apply brick pattern to facade if enabled
        else if (showBrickPattern && ((isFrontBack && brickOnFacade && (isDoorLevel ? brickOnGroundFloor : true)))) {
          // Only apply to even rows for spacing
          if (y % 2 === 0) {
            // Skip entire sections based on randomEraseBricks value
            // Use a larger scale pattern to determine areas to skip
            const sectionX = Math.floor(x / 4); // Create 4x4 sections
            const sectionY = Math.floor(y / 4);
            
            // Generate a consistent random value for this section
            const sectionSeed = (sectionX * 1000 + sectionY * 10);
            const sectionRandom = Math.sin(sectionSeed) * 10000 % 1;
            
            // Skip this section entirely if the random value is below our threshold
            // When randomEraseBricks is 0, never skip sections
            const shouldSkipSection = randomEraseBricks > 0 && sectionRandom < randomEraseBricks;
            
            if (!shouldSkipSection) {
              // Create a brick pattern - each brick is 2x1 (width x height)
              const rowOffset = (y % 4 === 0) ? 0 : 1; // Offset alternate rows
              const brickIndex = Math.floor(x / 2); // 2-unit wide bricks
              const brickPosition = (brickIndex + rowOffset) % 2;
              
              // Only change color for brick positions (not mortar)
              if (brickPosition !== 0) {
                // Apply fine-grained random erasure
                const brickRandom = Math.random();
                const shouldKeepBrick = brickRandom >= randomEraseFine;
                
                // Use brick color if we're keeping this brick
                if (shouldKeepBrick) {
                  // Use brick color for the bricks
                  wallMaterial = materials.brick;
                }
              }
            }
          }
        }
        
        const wall = new THREE.Mesh(sharedCubeGeometry, wallMaterial);
        wall.position.set(x * cubeSize, y * cubeSize + yOffset, zPos);
        wall.name = 'dynamic';
        wallGroup.add(wall);

        if (showEdges) {
          const line = new THREE.LineSegments(sharedEdgesGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
          line.position.copy(wall.position);
          wallGroup.add(line);
        }
      }
    }
    
    group.add(wallGroup);
  };

  // Loop through each building level
  for (let i = 0; i < levelNum; i++) {
    const yOffset = i === 0 ? 0 : groundFloorHeight * cubeSize + (i - 1) * levelHeight * cubeSize;
    const currentLevelHeight = i === 0 ? groundFloorHeight : levelHeight;
    const frontMaterial = i === 0 ? materials.brown : materials.facade;
    const backMaterial = i === 0 ? materials.brown : materials.courtyard;

    // Determine door position
    let doorX;
    if (doorPosition === 'left') {
      doorX = 1;
    } else if (doorPosition === 'right') {
      doorX = width - doorWidth - 1;
    } else {
      doorX = Math.floor((width - doorWidth) / 2);
    }

    // Track window positions to avoid placing facade voxels there
    let windowPositions = new Set();
    
    // Add windows on upper floors first (to know where to skip facade voxels)
    if (i > 0) {
      windowPositions = addWindows(group, yOffset, materials.windowColorB);
    }

    // Front wall - after windows so we can skip inset areas
    createWall(frontMaterial, 0, true, yOffset, currentLevelHeight, doorX, i === 0, windowPositions);

    // Back wall - after windows so we can skip inset areas
    createWall(backMaterial, (depth - 1) * cubeSize, true, yOffset, currentLevelHeight, doorX, i === 0, windowPositions);

    // Add doors on ground floor
    if (i === 0) {
      const doorY = 0;
      const doorZFront = 0;
      const doorZBack = (depth - 1) * cubeSize;
      const doorFront = createDoor(doorX * cubeSize, doorY, doorZFront, doorWidth, doorHeight, materials.door);
      const doorBack = createDoor(doorX * cubeSize, doorY, doorZBack, doorWidth, doorHeight, materials.door);
      group.add(doorFront);
      group.add(doorBack);
      
      // Add vitrine if enabled (from ground up to the top of the door)
      if (showVitrine) {
        // Add vitrine only to the back of the building
        for (let y = 0; y < doorHeight; y++) {
          for (let x = vitrineOffset; x < width - vitrineOffset; x++) { // Use vitrineOffset for edge gap
            // Skip where the door is (plus vitrineOffset voxel spacing on each side)
            if (x >= doorX - vitrineOffset && x < doorX + doorWidth + vitrineOffset) {
              continue;
            }
            
            // Get the effective width of this vitrine section
            const vitrineWidth = (width - vitrineOffset * 2) - (doorWidth + vitrineOffset * 2);
            
            // Check if this is a frame position when frames are enabled
            let isFrame = false;
            
            if (showVitrineFrames) {
              // Always include the outer border - all 4 sides when division = 1
              isFrame = (
                x === vitrineOffset || // Left edge
                x === width - vitrineOffset - 1 || // Right edge
                y === 0 || // Bottom edge
                y === doorHeight - 1 || // Top edge
                // Door adjacent edges - make sure to include vertical frames next to the door
                (vitrineFrameDivision === 1 && (
                  x === doorX - vitrineOffset - 1 || // Left edge of door
                  x === doorX + doorWidth + vitrineOffset // Right edge of door
                ))
              );
              
              if (!isFrame && vitrineFrameDivision > 1) {
                // Calculate spacing for vertical dividers based on the division parameter
                // First identify the left and right sections relative to the door
                let sectionWidth = 0;
                let sectionStart = 0;
                
                if (x < doorX - vitrineOffset) {
                  // Left section
                  sectionWidth = doorX - vitrineOffset * 2;
                  sectionStart = vitrineOffset;
                } else {
                  // Right section
                  sectionWidth = width - vitrineOffset - (doorX + doorWidth + vitrineOffset);
                  sectionStart = doorX + doorWidth + vitrineOffset;
                }
                
                // Now add dividers based on the section width and frame division
                if (sectionWidth > 0) {
                  // Calculate the spacing for dividers
                  const spacing = Math.floor(sectionWidth / vitrineFrameDivision);
                  
                  // Check if current position is a divider position
                  if (spacing > 0) {
                    // Position is a divider if it's at a division point
                    for (let i = 1; i < vitrineFrameDivision; i++) {
                      if (x === sectionStart + i * spacing) {
                        isFrame = true;
                        break;
                      }
                    }
                  }

                  // Always add frames adjacent to door
                  if (x === doorX - vitrineOffset - 1 || x === doorX + doorWidth + vitrineOffset) {
                    isFrame = true;
                  }
                }
              }
            }
            
            // Use the appropriate material based on whether this is a frame or not
            const material = isFrame ? materials.vitrineFrame : materials.vitrine;
            
            // Add vitrine cubes for back side only
            const vitrineBack = createVitrine(x * cubeSize, y * cubeSize, (depth - 1) * cubeSize, material);
            group.add(vitrineBack);
            
            if (showEdges) {
              const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
              const edges = new THREE.EdgesGeometry(geometry);
              const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
              line.position.copy(vitrineBack.position);
              group.add(line);
            }
          }
        }
        
        // Add vitrine awning if enabled
        if (showVitrineAwning) {
          const awningY = doorHeight * cubeSize; // Position at the top of the door/vitrine
          for (let x = vitrineOffset; x < width - vitrineOffset; x++) { // Use vitrineOffset for edge gap
            // Skip where the door is (plus vitrineOffset voxel spacing on each side)
            if (x >= doorX - vitrineOffset && x < doorX + doorWidth + vitrineOffset) {
              continue;
            }
            
            // Add height to the awning
            for (let h = 0; h < vitrineAwningHeight; h++) {
              // Create awning at different depths
              for (let d = 0; d < vitrineAwningDepth; d++) {
                const vitrineAwning = createAwning(
                  x * cubeSize, 
                  awningY + h, // Add height
                  (depth - 1) * cubeSize + 1 + d, // Extend out from the back facade
                  materials.vitrineAwning // Use custom vitrineAwning material
                );
                group.add(vitrineAwning);
                
                if (showEdges) {
                  const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
                  const edges = new THREE.EdgesGeometry(geometry);
                  const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                  line.position.copy(vitrineAwning.position);
                  group.add(line);
                }
              }
            }
          }
        }
      }
    }

    // Side walls
    const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    for (let z = 1; z < depth - 1; z++) {
      for (let y = 0; y < currentLevelHeight; y++) {
        const sideWallMaterial = (showLevelLines && y === currentLevelHeight - 1)
          ? materials.levelLineSide
          : materials.sideWall;
          
        // If brick pattern is enabled, determine the brick material
        let leftWallMaterial = sideWallMaterial;
        let rightWallMaterial = sideWallMaterial;
        
        // Only apply brick pattern to side walls if enabled
        if (showBrickPattern && brickOnSideWalls && y !== currentLevelHeight - 1 && y % 2 === 0) {
          // Skip entire sections based on randomEraseBricks value
          // Use a larger scale pattern to determine areas to skip
          const sectionZ = Math.floor(z / 4); // Create 4x4 sections
          const sectionY = Math.floor(y / 4);
          
          // Generate a consistent random value for this section
          const sectionSeed = (sectionZ * 1000 + sectionY * 10);
          const sectionRandom = Math.sin(sectionSeed) * 10000 % 1;
          
          // Skip this section entirely if the random value is below our threshold
          // When randomEraseBricks is 0, never skip sections
          const shouldSkipSection = randomEraseBricks > 0 && sectionRandom < randomEraseBricks;
          
          if (!shouldSkipSection) {
            // Create a brick pattern - each brick is 2x1 (width x height)
            const rowOffset = (y % 4 === 0) ? 0 : 1; // Adjust offset pattern for skipped rows
            // Integer division by 2 to create bricks that are 2 units wide
            const brickIndex = Math.floor(z / 2);
            // Add row offset to create alternating pattern between rows
            const brickPosition = (brickIndex + rowOffset) % 2;
            
            // Only change color for brick positions (not mortar)
            if (brickPosition !== 0) {
              // Apply fine-grained random erasure
              const brickRandom = Math.random();
              const shouldKeepBrick = brickRandom >= randomEraseFine;
              
              // Use brick color if we're keeping this brick
              if (shouldKeepBrick) {
                // Use the brick color for the actual bricks
                leftWallMaterial = materials.brick;
                rightWallMaterial = materials.brick;
              }
            }
          }
        }
        
        // Left wall
        const leftWall = new THREE.Mesh(geometry, leftWallMaterial);
        leftWall.position.set(0, y * cubeSize + yOffset, z * cubeSize);
        leftWall.name = 'dynamic';
        group.add(leftWall);

        // Right wall
        const rightWall = new THREE.Mesh(geometry, rightWallMaterial);
        rightWall.position.set((width - 1) * cubeSize, y * cubeSize + yOffset, z * cubeSize);
        rightWall.name = 'dynamic';
        group.add(rightWall);
        
        if (showEdges) {
          const createEdges = (wall) => {
            const edges = new THREE.EdgesGeometry(geometry);
            const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
            line.position.copy(wall.position);
            group.add(line);
          };
          
          createEdges(leftWall);
          createEdges(rightWall);
        }
      }
    }
    
    // Add side windows if enabled (only for upper floors)
    if (i > 0 && (showLeftSideWindows || showRightSideWindows)) {
      const sideWindowYOffset = 5; // Same as in addWindows function
      
      // Get window dimensions from current config
      let windowWidth = 8;
      let numWindows = 4;
      let spacing = 2;
      
      // Adjust window dimensions based on config for consistency
      if (windowConfig === 'config5') {
        windowWidth = 6;
        numWindows = 5;
        spacing = 2;
      } else if (windowConfig === 'config2Large') {
        const margin = 4;
        const availableWidth = width - margin * 3;
        windowWidth = Math.floor(availableWidth / 2);
        numWindows = 2;
        spacing = margin;
      } else if (windowConfig === 'config6') {
        windowWidth = 8;
        numWindows = 5;
        spacing = 0;
      }
      
      // Calculate window spacing for side walls
      const shift = windowConfig === 'config6' ? 1 : 0;
      // Position windows very close to the back edge
      const totalWindowSpace = numWindows * windowWidth + (numWindows - 1) * spacing;
      let startZ = depth - totalWindowSpace - 2; // Just 2 voxels from the back edge
      
      // Ensure startZ is not negative
      startZ = Math.max(2, startZ); // At least 2 voxels from the front edge
      
      // Left side windows
      if (showLeftSideWindows) {
        for (let i = 0; i < numWindows; i++) {
          for (let z = 0; z < windowWidth; z++) {
            for (let y = windowBottom; y < windowTop; y++) {
              const isFrame = showWindowFrames && (
                z === 0 || // left edge
                z === windowWidth - 1 || // right edge
                y === windowBottom || // bottom edge
                y === windowTop - 1 // top edge
              );
              
              const mat = isFrame ? materials.windowFrame : 
                        (z < windowWidth / 2 ? materials.windowColorA : materials.windowColorB);
              
              const windowCube = createWindow(
                0, // X position is always 0 for left wall
                yOffset + sideWindowYOffset + y,
                startZ + i * (windowWidth + spacing) + z,
                mat,
                isFrame
              );
              
              group.add(windowCube);
              
              if (showEdges) {
                const edges = new THREE.EdgesGeometry(geometry);
                const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                line.position.copy(windowCube.position);
                group.add(line);
              }
            }
          }
          
          // Add balconies if enabled
          if (showBalconies) {
            for (let h = 0; h < balconyHeight; h++) {
              const currentBalconyY = yOffset + sideWindowYOffset + windowBottom - 1 - h;
              const balconyStartZ = startZ + i * (windowWidth + spacing) - balconySideOffset;
              const balconyWindowWidth = windowWidth + (balconySideOffset * 2);
              
              for (let z = 0; z < balconyWindowWidth; z++) {
                for (let d = 0; d < balconyDepth; d++) {
                  const balcony = createBalcony(
                    -1 - d, // Extend outward from left wall
                    currentBalconyY,
                    balconyStartZ + z,
                    materials.balcony
                  );
                  group.add(balcony);
                  
                  if (showEdges) {
                    const edges = new THREE.EdgesGeometry(geometry);
                    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                    line.position.copy(balcony.position);
                    group.add(line);
                  }
                }
              }
            }
          }
          
          // Add awnings if enabled
          if (showAwnings) {
            for (let h = 0; h < awningHeight; h++) {
              const currentAwningY = yOffset + sideWindowYOffset + windowTop + h;
              const awningStartZ = startZ + i * (windowWidth + spacing) - awningSideOffset;
              const awningWindowWidth = windowWidth + (awningSideOffset * 2);
              
              for (let z = 0; z < awningWindowWidth; z++) {
                for (let d = 0; d < awningDepth; d++) {
                  const awning = createAwning(
                    -1 - d, // Extend outward from left wall
                    currentAwningY,
                    awningStartZ + z,
                    materials.awning
                  );
                  group.add(awning);
                  
                  if (showEdges) {
                    const edges = new THREE.EdgesGeometry(geometry);
                    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                    line.position.copy(awning.position);
                    group.add(line);
                  }
                }
              }
            }
          }
        }
      }
      
      // Right side windows
      if (showRightSideWindows) {
        for (let i = 0; i < numWindows; i++) {
          for (let z = 0; z < windowWidth; z++) {
            for (let y = windowBottom; y < windowTop; y++) {
              const isFrame = showWindowFrames && (
                z === 0 || // left edge
                z === windowWidth - 1 || // right edge
                y === windowBottom || // bottom edge
                y === windowTop - 1 // top edge
              );
              
              const mat = isFrame ? materials.windowFrame : 
                        (z < windowWidth / 2 ? materials.windowColorA : materials.windowColorB);
              
              const windowCube = createWindow(
                (width - 1), // X position is always width-1 for right wall
                yOffset + sideWindowYOffset + y,
                startZ + i * (windowWidth + spacing) + z,
                mat,
                isFrame
              );
              
              group.add(windowCube);
              
              if (showEdges) {
                const edges = new THREE.EdgesGeometry(geometry);
                const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                line.position.copy(windowCube.position);
                group.add(line);
              }
            }
          }
          
          // Add balconies if enabled
          if (showBalconies) {
            for (let h = 0; h < balconyHeight; h++) {
              const currentBalconyY = yOffset + sideWindowYOffset + windowBottom - 1 - h;
              const balconyStartZ = startZ + i * (windowWidth + spacing) - balconySideOffset;
              const balconyWindowWidth = windowWidth + (balconySideOffset * 2);
              
              for (let z = 0; z < balconyWindowWidth; z++) {
                for (let d = 0; d < balconyDepth; d++) {
                  const balcony = createBalcony(
                    width + d, // Extend outward from right wall
                    currentBalconyY,
                    balconyStartZ + z,
                    materials.balcony
                  );
                  group.add(balcony);
                  
                  if (showEdges) {
                    const edges = new THREE.EdgesGeometry(geometry);
                    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                    line.position.copy(balcony.position);
                    group.add(line);
                  }
                }
              }
            }
          }
          
          // Add awnings if enabled
          if (showAwnings) {
            for (let h = 0; h < awningHeight; h++) {
              const currentAwningY = yOffset + sideWindowYOffset + windowTop + h;
              const awningStartZ = startZ + i * (windowWidth + spacing) - awningSideOffset;
              const awningWindowWidth = windowWidth + (awningSideOffset * 2);
              
              for (let z = 0; z < awningWindowWidth; z++) {
                for (let d = 0; d < awningDepth; d++) {
                  const awning = createAwning(
                    width + d, // Extend outward from right wall
                    currentAwningY,
                    awningStartZ + z,
                    materials.awning
                  );
                  group.add(awning);
                  
                  if (showEdges) {
                    const edges = new THREE.EdgesGeometry(geometry);
                    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
                    line.position.copy(awning.position);
                    group.add(line);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  // Add the roof if enabled
  if (showRoof) {
    const roofY = (groundFloorHeight + (levelNum - 1) * levelHeight) * cubeSize;
    const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    
    // Create roof base layer
    for (let x = 0; x < width; x++) {
      for (let z = -roofAwning; z < depth + roofAwning; z++) {
        const roofVoxel = new THREE.Mesh(geometry, materials.roof);
        roofVoxel.position.set(x * cubeSize, roofY, z * cubeSize);
        roofVoxel.name = 'dynamic';
        group.add(roofVoxel);

        if (showEdges) {
          const edges = new THREE.EdgesGeometry(geometry);
          const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
          line.position.copy(roofVoxel.position);
          group.add(line);
        }
      }
    }

    // Create roof thickness/guardrail if thickness > 0
    if (roofThickness > 0) {
      for (let h = 1; h <= roofThickness; h++) {
        // Front edge
        for (let x = 0; x < width; x++) {
          const roofVoxel = new THREE.Mesh(geometry, materials.guardrail);
          roofVoxel.position.set(x * cubeSize, roofY + (h * cubeSize), -roofAwning * cubeSize);
          roofVoxel.name = 'dynamic';
          group.add(roofVoxel);
        }

        // Back edge
        for (let x = 0; x < width; x++) {
          const roofVoxel = new THREE.Mesh(geometry, materials.guardrail);
          roofVoxel.position.set(x * cubeSize, roofY + (h * cubeSize), (depth - 1 + roofAwning) * cubeSize);
          roofVoxel.name = 'dynamic';
          group.add(roofVoxel);
        }

        // Left edge
        for (let z = -roofAwning + 1; z < depth + roofAwning - 1; z++) {
          const roofVoxel = new THREE.Mesh(geometry, materials.guardrail);
          roofVoxel.position.set(0, roofY + (h * cubeSize), z * cubeSize);
          roofVoxel.name = 'dynamic';
          group.add(roofVoxel);
        }

        // Right edge
        for (let z = -roofAwning + 1; z < depth + roofAwning - 1; z++) {
          const roofVoxel = new THREE.Mesh(geometry, materials.guardrail);
          roofVoxel.position.set((width - 1) * cubeSize, roofY + (h * cubeSize), z * cubeSize);
          roofVoxel.name = 'dynamic';
          group.add(roofVoxel);
        }
      }
    }

    // Add roof access structure (stairwell housing)
    if (showRoof && showRoofAccess && roofAccessWidth > 0 && roofAccessHeight > 0 && roofAccessDepth > 0) {
      const maxX = (width - roofAccessWidth) * cubeSize; // Maximum distance from right edge
      const x = maxX - roofAccessOffset * cubeSize;
      const y = roofY + cubeSize; // Just above the roof
      
      // Position the cube more towards the front (smaller Z value)
      // Use 1/3 of depth instead of 1/2 to move it forward
      const z = (depth / 3) * cubeSize;
      
      // Validate door dimensions
      const validDoorWidth = Math.min(roofAccessDoorWidth, roofAccessWidth - 2);
      const validDoorHeight = Math.min(roofAccessDoorHeight, roofAccessHeight - 2);
      
      // Calculate door position (centered on back face of roof access)
      const doorX = x + (roofAccessWidth * cubeSize - validDoorWidth * cubeSize) / 2;
      const doorY = y;
      const doorZ = z + roofAccessDepth * cubeSize / 2; // Changed to back face
      
      // Create the roof access structure
      for (let i = 0; i < roofAccessWidth; i++) {
        for (let j = 0; j < roofAccessHeight; j++) {
          for (let k = 0; k < roofAccessDepth; k++) {
            // Skip voxels where the door will be
            const currentX = x + i * cubeSize;
            const currentY = y + j * cubeSize;
            const currentZ = z - roofAccessDepth * cubeSize / 2 + k * cubeSize;
            
            // Check if this position is part of the door (back face only)
            const isDoorPosition = (
              k === roofAccessDepth - 1 && // Back face only (changed from front)
              currentX >= doorX && 
              currentX < doorX + validDoorWidth * cubeSize &&
              currentY >= doorY && 
              currentY < doorY + validDoorHeight * cubeSize
            );
            
            if (isDoorPosition) {
              // Add a door voxel instead of a regular roof access voxel
              const doorVoxel = new THREE.Mesh(sharedCubeGeometry, materials.roofAccessDoor);
              doorVoxel.position.set(currentX, currentY, currentZ);
              doorVoxel.name = 'dynamic';
              group.add(doorVoxel);
              
              if (showEdges) {
                const line = new THREE.LineSegments(sharedEdgesGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
                line.position.copy(doorVoxel.position);
                group.add(line);
              }
            } else {
              // Add a regular roof access voxel
              const accessVoxel = new THREE.Mesh(sharedCubeGeometry, materials.roofAccess);
              accessVoxel.position.set(currentX, currentY, currentZ);
              accessVoxel.name = 'dynamic';
              group.add(accessVoxel);
              
              if (showEdges) {
                const line = new THREE.LineSegments(sharedEdgesGeometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
                line.position.copy(accessVoxel.position);
                group.add(line);
              }
            }
          }
        }
      }
    }
  }

  return group;
};

export default {
  createWallsGroup
};
